/**
 * link-tracking.js — storefront link/CTA click tracking (producer half)
 * =====================================================================
 *
 * One delegated click listener that captures every qualifying link click and
 * sends it to GA4 via `window.gtag` — the Google & YouTube channel injects its
 * Google tag into the MAIN window at runtime (via content_for_header), so we
 * send through the channel's own GA4 instance with `send_to` pinned to our
 * stream. That gets full session semantics, page metadata, device/geo
 * dimensions, key-event registration, and Consent Mode for free.
 *
 * gtag is the ONLY transport — no gtag, no hit. Deliberate: when gtag is
 * absent it is usually the visitor's choice (blocker, privacy signal, denied
 * consent), and a Measurement Protocol fallback would circumvent that with a
 * fully-identified hit; it would also land session-less, since MP hits cannot
 * start sessions and a gtag-blocked visit has none. (An earlier fallback via
 * Shopify.analytics.publish → web-pixels/ga4-custom-events.pixel.js was
 * removed for exactly those reasons — the pixel file remains in-repo, dormant,
 * as the pattern for future checkout-scope events.)
 *
 * Defect reporting (design: docs/superpowers/specs/2026-10-14-storefront-sentry-
 * tracking-defects-design.md): this file is the only code that knows the
 * link_click contract, so it reports violations itself. A creative link (one
 * with data-creative-slot, or inside a data-section-alias element) must yield
 * the GA4 event parameters listed in CONTRACT below. A link that does not is
 * ONE TrackingError per click with the code missing_event_parameters. Its
 * context holds the GA4 payload as built (missing parameters present as null),
 * the list of missing parameters and one hint per missing parameter, so a
 * single Sentry event shows what was lost beside what was populated. The list
 * is also on the error as err.missing_event_parameters and becomes a tag in
 * Sentry. A gtag that throws is one TrackingError with the code gtag_error and
 * the payload as its context. Errors go to window.HighpassSentry.report
 * (sentry-report.js). The GA4 hit always goes first and is never changed by a
 * violation; the click always navigates.
 *
 * Design notes:
 *  - Event DELEGATION (one listener on document) so links injected after load
 *    — carousel slides, quick view, cart drawer — are covered without rebinding.
 *  - CAPTURE phase so we send before the click triggers navigation.
 *  - This file only reads the DOM and emits the event. Identity, session, and
 *    consent all live in gtag.
 *
 * Published payload (`link_click`), already shaped as GA4 params:
 *   { link_url, link_text, creative_slot, creative_name, creative_description,
 *     section_type, section_alias, section_id, outbound, debug_mode }
 *
 * `debug_mode` is producer-decided (see isDebug): automatic on non-main themes,
 * opt-in on production via localStorage "ga4_debug"="1". It routes the hit to
 * GA4 DebugView; gtag handles everything else.
 */
(function () {
  // The Google & YouTube channel's GA4 stream (public id, visible in page
  // source). `send_to` pins our events to it so they don't also route to the
  // channel's Ads (AW-…) container.
  const GA4_MEASUREMENT_ID = "G-N2LR08TK01";

  // Error kinds. One missing_event_parameters error covers every parameter
  // missing on a click; the Sentry fingerprint is the code alone (see sentry-report.js).
  const CODES = Object.freeze(["missing_event_parameters", "gtag_error"]);

  // The link_click contract, and the only place that says what is missing:
  // every creative link must yield these GA4 event parameters, in this order.
  // `isMissing(facts)` is true when the parameter cannot be sent; `facts` holds
  // what buildPayload read ({ creativeName, creativeDescription, creativeSlot,
  // sectionAlias, wrapper }). `setting` names what a merchant edits to fix it
  // and `file` the file that renders it; both feed the message and the hints.
  const CONTRACT = Object.freeze([
    {
      parameter: "creative_name",
      isMissing: function (facts) {
        return !facts.creativeName;
      },
      setting: "the slide heading",
      file: "sections/slideshow.liquid",
    },
    {
      parameter: "creative_description",
      isMissing: function (facts) {
        return !facts.creativeDescription;
      },
      setting: "the slide description",
      file: "sections/slideshow.liquid",
    },
    {
      // Only a link that claims a slot must sit in an aliased section.
      parameter: "section_alias",
      isMissing: function (facts) {
        return facts.creativeSlot != null && facts.sectionAlias == null;
      },
      setting: 'the section\'s "Analytics alias" setting',
      file: "sections/slideshow.liquid",
    },
    {
      // No wrapper also costs section_id, which the payload shows as null.
      parameter: "section_type",
      isMissing: function (facts) {
        return !facts.wrapper;
      },
      setting:
        "the `.shopify-section` wrapper that Shopify renders around every section",
      file: "layout/theme.liquid",
    },
  ]);

  // A tracking failure with a stable code. `context` is what a person needs
  // to find the link: the GA4 payload, no customer data, ever. For
  // missing_event_parameters, `context.missing_event_parameters` lists the
  // missing GA4 event parameters and is also set on the error as
  // `missing_event_parameters`, which the reporter reads.
  class TrackingError extends Error {
    constructor(code, message, context) {
      super(message);
      this.name = "TrackingError";
      this.code = code;
      this.context = context || {};
      if (this.context.missing_event_parameters) {
        this.missing_event_parameters = this.context.missing_event_parameters;
      }
    }
  }

  // gtag presence is checked PER CLICK (not at load): the channel injects it
  // after initial scripts run. Absent gtag → no hit, by design (see header).
  function sendLinkClick(params) {
    if (typeof window.gtag !== "function") return false;
    const clean = { send_to: GA4_MEASUREMENT_ID };
    for (const key of Object.keys(params)) {
      if (params[key] != null) clean[key] = params[key];
    }
    window.gtag("event", "link_click", clean);
    return clean;
  }

  // Textarea-backed settings (slide headings/descriptions) can carry newlines;
  // collapse all whitespace so GA4 dimension values stay single-line. Empty → null.
  function cleanText(value) {
    const text = (value || "").replace(/\s+/g, " ").trim();
    return text || null;
  }

  // Debug is decided HERE (producer): `debug_mode` rides the payload and
  // routes the hit to GA4 DebugView. Automatic on preview /
  // unpublished themes (Shopify.theme.role !== "main"); on production, opt a
  // single browser in via localStorage.setItem("ga4_debug", "1").
  function isDebug() {
    const theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.role && theme.role !== "main") {
      return true;
    }
    try {
      return window.localStorage.getItem("ga4_debug") === "1";
    } catch (e) {
      return false;
    }
  }

  // One sentence for the Sentry title. One missing parameter names its source;
  // two or more list only the parameters, because the hints object pairs each
  // parameter with its source. Built from the table, so it never carries
  // link-specific text.
  function missingMessage(rows) {
    const names = rows.map(function (row) {
      return "`" + row.parameter + "`";
    });
    if (rows.length === 1) {
      return (
        "link_click sent without the event parameter " +
        names[0] +
        " (" +
        rows[0].setting +
        " in " +
        rows[0].file +
        ")"
      );
    }
    return "link_click sent without the event parameters " + names.join(", ");
  }

  // Reads the DOM around one link and returns the GA4 payload plus the list of
  // contract violations. Never throws for a missing attribute: a violation is
  // data, and the payload is best-effort so GA4 still gets the click.
  function buildPayload(link) {
    const violations = [];

    // The `.shopify-section` wrapper is Shopify-generated and always present.
    // Its class carries the theme's `section-<type>` convention (e.g.
    // `section-slideshow`); reading it off THIS element avoids the
    // `section-spacing` utility classes, which sit on inner elements.
    const wrapper = link.closest(".shopify-section");
    const sectionTypeClass = wrapper
      ? Array.prototype.find.call(wrapper.classList, function (c) {
          return c.indexOf("section-") === 0;
        })
      : null;

    // `data-section-alias` is a per-instance label declared in the section's
    // "Analytics alias" setting (e.g. "hero") — sits on an element inside the
    // section, so walk up from the link like the wrapper lookup above.
    const aliasHost = link.closest("[data-section-alias]");

    const creativeSlot = link.dataset.creativeSlot || null; // from data-creative-slot
    const creativeName = cleanText(link.dataset.creativeName); // slide heading
    const creativeDescription = cleanText(link.dataset.creativeDescription); // slide description
    const sectionAlias = aliasHost ? aliasHost.dataset.sectionAlias : null;
    const linkText = (link.textContent || "").trim();

    // Payload is already shaped as GA4 params — gtag sends them as-is,
    // so these ARE the GA4 param names.
    const payload = {
      link_url: link.href,
      link_text: linkText,
      creative_slot: creativeSlot,
      creative_name: creativeName,
      creative_description: creativeDescription,
      section_type: sectionTypeClass
        ? sectionTypeClass.replace(/^section-/, "")
        : null,
      section_alias: sectionAlias,
      section_id: wrapper ? wrapper.id : null,
      outbound: link.hostname !== window.location.hostname,
      debug_mode: isDebug() || null, // null → stripped before send (prod)
    };

    // Only creative links are under contract: navigation, footer and product
    // card links carry none of these attributes on purpose.
    const isCreative = creativeSlot != null || sectionAlias != null;
    if (isCreative) {
      const facts = {
        creativeName: creativeName,
        creativeDescription: creativeDescription,
        creativeSlot: creativeSlot,
        sectionAlias: sectionAlias,
        wrapper: wrapper,
      };
      const missing = CONTRACT.filter(function (row) {
        return row.isMissing(facts);
      });
      if (missing.length) {
        const hints = {};
        for (const row of missing) {
          hints[row.parameter] =
            "Rendered by " + row.file + " from " + row.setting + ".";
        }
        hints.docs =
          "Contract and fixes: docs/analytics/link-click-tracking.md (Defect reporting).";
        violations.push(
          new TrackingError(
            "missing_event_parameters",
            missingMessage(missing),
            {
              payload: Object.assign({}, payload, {
                link_text: payload.link_text.slice(0, 200),
              }),
              missing_event_parameters: missing.map(function (row) {
                return row.parameter;
              }),
              hints: hints,
            },
          ),
        );
      }
    }

    return { payload: payload, violations: violations };
  }

  // Hands an error to sentry-report.js when it is loaded; otherwise warns in
  // debug mode. Returns false rather than throwing: reporting must never cost a hit.
  function report(err, opts) {
    const sentry = window.HighpassSentry;
    const debug = isDebug();
    try {
      if (sentry && typeof sentry.report === "function") {
        return sentry.report(err, Object.assign({ debug: debug }, opts));
      }
      if (debug) console.warn("[link-tracking] reporter not loaded", err);
    } catch (e) {
      if (debug) console.warn("[link-tracking] report failed", e);
    }
    return false;
  }

  function handleClick(event) {
    let built = null;
    try {
      // Nearest real link. `data-track="false"` opts a noisy link back out
      // (e.g. pagination, faceted-filter anchors). Synthetic clicks can target
      // a text node or the document, which have no `closest`.
      const target = event.target;
      if (
        !target ||
        target.nodeType !== 1 ||
        typeof target.closest !== "function"
      )
        return;
      const link = target.closest("a[href]");
      if (!link || link.dataset.track === "false") return;

      built = buildPayload(link);
      let sent;
      try {
        sent = sendLinkClick(built.payload);
      } catch (e) {
        throw new TrackingError(
          "gtag_error",
          "gtag failed while sending link_click: " + (e && e.message),
          {
            payload: Object.assign({}, built.payload, {
              link_text: built.payload.link_text.slice(0, 200),
            }),
          },
        );
      }
      // Same gate as the hit: no analytics for this visitor, no report either.
      if (!sent) return;
      for (const violation of built.violations) {
        report(violation, {
          tags: {
            section_alias: built.payload.section_alias,
            section_type: built.payload.section_type,
            creative_slot: built.payload.creative_slot,
          },
        });
      }
    } catch (err) {
      // Anything unexpected (including gtag_error) is reported and swallowed:
      // the click must still navigate. Same gate as the hit: no gtag, no report.
      if (typeof window.gtag !== "function") return;
      if (built) {
        report(err, {
          tags: {
            section_alias: built.payload.section_alias,
            section_type: built.payload.section_type,
            creative_slot: built.payload.creative_slot,
          },
          // A TrackingError already carries its payload (capped) in its context.
          extra: err instanceof TrackingError ? {} : { payload: built.payload },
        });
      } else {
        report(err, { extra: {} });
      }
    }
  }

  document.addEventListener("click", handleClick, { capture: true });

  window.HighpassLinkTracking = {
    TrackingError: TrackingError,
    CODES: CODES,
    CONTRACT: CONTRACT,
    buildPayload: buildPayload,
    handleClick: handleClick,
  };
})();
